// Date-bucket group-by helpers — the `field:granularity` codec and the
// locale-aware label formatter consumed by SearchPanel chips, ListView group
// headers, and KanbanView column titles.
//
// The wire format is Odoo-style: a groupby entry on a `date`/`datetime` field
// optionally carries a `:granularity` suffix (`order_date:month`). The backend
// (presentation.action_records) strips the suffix when generating the SQL
// ORDER BY because the bucketing itself is computed client-side from the raw
// timestamp value.

import { format } from "date-fns";

export type DateGranularity = "year" | "quarter" | "month" | "week" | "day";

export const DATE_GRANULARITIES: ReadonlyArray<DateGranularity> = [
    "year",
    "quarter",
    "month",
    "week",
    "day",
];

export const DEFAULT_DATE_GRANULARITY: DateGranularity = "month";

const VALID = new Set<string>(DATE_GRANULARITIES);

export interface GroupByEntry {
    field: string;
    granularity?: DateGranularity;
}

// Parse a single group-by entry. Returns `{field, granularity?}`.
// Plain field names (no `:`) return `{field}` only.
// Unknown granularities collapse to `{field}` so a stale URL doesn't break
// the UI — caller can decide whether to default to month.
export function parseGroupByEntry(entry: string): GroupByEntry {
    const idx = entry.indexOf(":");
    if (idx < 0) return { field: entry };
    const field = entry.slice(0, idx);
    const granularity = entry.slice(idx + 1).toLowerCase();
    if (!VALID.has(granularity)) return { field };
    return { field, granularity: granularity as DateGranularity };
}

// Build the wire-form entry. `granularity` undefined → bare `field`.
export function buildGroupByEntry(spec: GroupByEntry): string {
    return spec.granularity ? `${spec.field}:${spec.granularity}` : spec.field;
}

// Convenience: pull the bare field name out of an entry without the bucket.
export function groupByEntryField(entry: string): string {
    return parseGroupByEntry(entry).field;
}

// Convenience: bucket suffix or undefined.
export function groupByEntryGranularity(entry: string): DateGranularity | undefined {
    return parseGroupByEntry(entry).granularity;
}

// Bucket a raw timestamp (UTC ISO string or Date) by granularity. Returns
// the canonical bucket KEY (used as the dedup/group key in client-side
// grouping) — distinct from the human label, which adds locale formatting.
//
// Keys are designed to be stable strings that sort correctly when ordered
// lexicographically and compare with raw === for bucket dedup:
//   year     → "2026"
//   quarter  → "2026-Q2"
//   month    → "2026-05"
//   week     → "2026-W22" (ISO week, padded)
//   day      → "2026-05-31"
export function bucketKey(value: Date | string | null | undefined, granularity: DateGranularity): string {
    if (value == null || value === "") return "";
    const date = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return "";
    switch (granularity) {
        case "year":
            return format(date, "yyyy");
        case "quarter":
            return `${format(date, "yyyy")}-Q${Math.floor(date.getMonth() / 3) + 1}`;
        case "month":
            return format(date, "yyyy-MM");
        case "week": {
            // ISO-week number (Mon-start). `date-fns` `format("II")` returns
            // 2-digit ISO week without the year context; we pair it with the
            // ISO-week-bearing year (`"RR"` → 4-digit) so end-of-December dates
            // that belong to next year's W01 sort correctly.
            const isoWeek = format(date, "II");
            const isoWeekYear = format(date, "RRRR");
            return `${isoWeekYear}-W${isoWeek}`;
        }
        case "day":
            return format(date, "yyyy-MM-dd");
    }
}

// Human-readable label for the bucket (locale-aware). Today this uses the
// device locale via `toLocaleString`; when LocaleManager wires user-pref
// locale + timezone formally, swap the impl here — callers will not change.
//
//   year     → "2026"
//   quarter  → "Q2 2026"
//   month    → "May 2026"
//   week     → "Week 22, 2026"
//   day      → "31 May 2026"
export function formatBucket(
    value: Date | string | null | undefined,
    granularity: DateGranularity,
): string {
    if (value == null || value === "") return "(empty)";
    const date = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) return String(value);
    switch (granularity) {
        case "year":
            return format(date, "yyyy");
        case "quarter":
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${format(date, "yyyy")}`;
        case "month":
            return format(date, "MMMM yyyy");
        case "week":
            return `Week ${format(date, "II")}, ${format(date, "RRRR")}`;
        case "day":
            return format(date, "d MMMM yyyy");
    }
}

// Human label for the granularity itself — used in the chip "field · Month ▾"
// and in the Radix DropdownMenu items.
export const GRANULARITY_LABELS: Record<DateGranularity, string> = {
    year: "Year",
    quarter: "Quarter",
    month: "Month",
    week: "Week",
    day: "Day",
};

// ─── Multi-granularity chip segments ────────────────────────────────────────
//
// A user can stack MULTIPLE granularities on the SAME date field to nest the
// grouping (Year > Quarter > Month > Week > Day or any subset). In the wire
// `groupBy: string[]`, that materialises as consecutive entries sharing the
// same bare field: `["order_date:year", "order_date:quarter", "order_date:month"]`.
//
// The SearchPanel chip renders one "segment" per run of consecutive same-field
// entries — `Order Date · Year › Quarter › Month` reads as one chip rather
// than the field name repeating three times.

export interface ChipSegmentEntry {
    entry: string;
    index: number;
    granularity?: DateGranularity;
}

export interface ChipSegment {
    field: string;
    /** Position in `groupBy` of the FIRST entry that started this segment. */
    startIndex: number;
    entries: ChipSegmentEntry[];
}

/**
 * Group consecutive same-field entries in `groupBy` into chip segments. Pure
 * derivation — non-consecutive same-field entries become separate segments so
 * the user can interleave fields without the dropdowns blurring chains.
 */
export function computeChipSegments(groupBy: string[]): ChipSegment[] {
    const segments: ChipSegment[] = [];
    groupBy.forEach((entry, index) => {
        const parsed = parseGroupByEntry(entry);
        const last = segments[segments.length - 1];
        if (last && last.field === parsed.field) {
            last.entries.push({ entry, index, granularity: parsed.granularity });
        } else {
            segments.push({
                field: parsed.field,
                startIndex: index,
                entries: [{ entry, index, granularity: parsed.granularity }],
            });
        }
    });
    return segments;
}

/**
 * Toggle a single granularity within a chip segment and return the new
 * `groupBy` array. Adding inserts in canonical hierarchical order; the
 * "remove the last granularity" call is a no-op (the SearchPanel disables
 * the checkbox at the UI layer so the user can never reach that state).
 */
export function toggleGranularityInSegment(
    groupBy: string[],
    segment: ChipSegment,
    granularity: DateGranularity,
): string[] {
    const existing = segment.entries
        .map((e) => e.granularity)
        .filter((g): g is DateGranularity => !!g);
    const hasIt = existing.includes(granularity);
    if (hasIt && existing.length === 1) return groupBy; // protect against empty segment
    let next: DateGranularity[];
    if (hasIt) {
        next = existing.filter((g) => g !== granularity);
    } else {
        next = [...existing, granularity];
    }
    // Canonical hierarchical order — Year > Quarter > Month > Week > Day —
    // regardless of pick order, so the chain always reads coarsest first.
    next.sort((a, b) => DATE_GRANULARITIES.indexOf(a) - DATE_GRANULARITIES.indexOf(b));

    const indices = segment.entries.map((e) => e.index);
    const minIdx = Math.min(...indices);
    const maxIdx = Math.max(...indices);
    const replacement = next.map((g) =>
        buildGroupByEntry({ field: segment.field, granularity: g }),
    );
    return [
        ...groupBy.slice(0, minIdx),
        ...replacement,
        ...groupBy.slice(maxIdx + 1),
    ];
}
